import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogTitle } from '@mui/material';

import {
    loadFinancialPaymentMethodDetailsByPaymentMethodType,
    loadFinancialPaymentMethodTypeDetails,
    loadFinancialRequestReasonDetailsByRequestType,
    loadFinancialRequestStatusDetails,
    loadFinancialRequestTypeDetails
} from '../../../../store/directusService';
import { loadParticipantDocumentDetails } from '../../../../store/documentService';
import { setErrorMessage } from '../../../../store/formsState';
import {
    clearParticipantAuditEventSearchData,
    updateFinancialIssueRequest,
    updateFinancialRequest
} from '../../../../store/participantService';
import { loadUserById, searchUsersByLoggedInUserServiceIds } from '../../../../store/userService';
import { reverseFormatDate } from '../../../../utils/dateFunctions';
import { getConfiguredItems, getNameFromId } from '../../../../utils/directusFunctions';
import { regExps } from '../../../../utils/formValidation/validationPatterns';
import { clearKeys } from '../../../../utils/objectUtils';
import { addEmailAddressAsNameToArray } from '../../../../utils/userArrayUtils';
import {
    ADVISER,
    hasRole,
    LOCAL_ADMIN,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import AuditFinancialRequestsModal from '../../../auditing/auditFinancialRequests/AuditFinancialRequestsModal';
import FormButton from '../../../formElements/Button';
import SingleSelect from '../../../formElements/SingleSelect';
import Switch from '../../../formElements/Switch';
import ESignatureModal from '../../../signatureDialog/ESignatureModal';
import LabelledTextField from '../../../ui/editors/LabelledTextField';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import DDLOptionPicker from '../../../ui/pickers/DDLOptionPicker';
import NoYesRadioPicker from '../../../ui/pickers/NoYesRadioPicker';

import GenerateFinancialRequestPDF from './GenerateFinancialRequestPDF';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';
import actions from '../../../ui/formActions/formActions.module.css';

const REASON_DETAILS_LIMIT = 500;
const AUTHORISER_NOTES_LIMIT = 500;

const financialRequestValidDocTypes = {
    evidence: 'Financial Request Evidence',
    authorisation: 'Financial Request Authorisation'
};

const EditFinancialRequest = ({ row, roles, onClose }) => {
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema)
    });

    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: null,
        code: null,
        adviserId: null,
        amount: null,
        approverId: null,
        requestTypeId: null,
        paymentMethodId: null,
        paymentMethodTypeId: null,
        paymentReasonId: null,
        dateIssued: null,
        dateRequested: null,
        statusChangeDate: '',
        taxNiIssued: false,
        reasonDetail: '',
        approverNote: '',
        statusId: null,
        participantId: null,
        authorisationDocumentId: null,
        evidenceDocumentId: null,
        requestProcessed: false,
        authoriserSignature: null
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const hasBeenIssued = !!row.dateIssued;
    const [keys, setKeys] = useState({
        adviser: '0',
        requestType: '1',
        requestReason: '2',
        paymentMethodType: '3',
        paymentMethod: '4',
        authorisationDocumentId: '5',
        evidenceDocumentId: '6'
    });
    const adminRoles = [QUALITY, SUPERUSER];
    const adviserRoles = [ADVISER, MANAGER, RECRUITMENT_MANAGER, SUPERUSER];
    const [advisers, setAdvisers] = useState([]);
    const [arrayFinancialRequestType, setArrayFinancialRequestType] = useState([]);
    const [arrayPaymentMethodType, setArrayPaymentMethodType] = useState([]);
    const [requestReasonRequired, setRequestReasonRequired] = useState(false);
    const [paymentMethodRequired, setPaymentMethodRequired] = useState(false);
    const [documents, setDocuments] = useState([]);

    const [approveDisable, setApproveDisable] = useState(false);
    const [declineDisable, setDeclineDisable] = useState(false);
    const [withdrawDisable, setWithdrawDisable] = useState(false);

    const [participantSignature, setParticipantSignature] = useState(null);
    const [participantSignatureDialogOpen, setParticipantSignatureDialogOpen] = useState(false);
    const [authorisersSignatureDialogOpen, setAuthorisersSignatureDialogOpen] = useState(false);
    const [showPdfPreview, setShowPdfPreview] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [isAuditable, setIsAuditable] = useState(false);

    const lastSubmittedRequestId = useRef(null);

    let payload;

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const financialPaymentMethodTypeDetails = useSelector(
        (state) => state.entities.directusService.financialPaymentMethodTypeDetails
    );
    const financialRequestStatusDetails = useSelector(
        (state) => state.entities.directusService.financialRequestStatusDetails
    );
    const financialRequestTypeDetails = useSelector(
        (state) => state.entities.directusService.financialRequestTypeDetails
    );
    const { financialRequestReasonDetails, financialPaymentMethodDetails } = useSelector(
        (state) => state.entities.directusService
    );
    const participantDocuments = useSelector(
        (state) => state.entities.documentService.participantDocumentDetails
    );
    const {
        number: documentsPageNumber,
        last: isLastDocumentsPage,
        totalElements: totalDocuments
    } = useSelector((state) => state.entities.documentService.participantDocumentDetailsMetaData);

    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const usersMetaData = useSelector(
        (state) => state.entities.userService.usersByLoggedInUserServiceIdsMetaData
    );

    const usersById = useSelector((state) => state.entities.userService.usersById);

    const statusName = useMemo(() => {
        return financialRequestStatusDetails
            .find((item) => item.id === row.statusId)
            ?.name.toLowerCase();
    }, [row.statusId]);

    // EVENT HANDLERS
    const handleAdviserChange = (chosenId) => {
        if (chosenId && chosenId !== row.adviserId) disableButtonsOnEdit();
        setNewEntry((prev) => ({ ...prev, adviserId: chosenId }));
    };

    const handleAmount = (e) => {
        disableButtonsOnEdit();
        setNewEntry((prev) => ({ ...prev, amount: e.target.value }));
    };

    const handleRequestTypeChange = (chosenId) => {
        if (chosenId && chosenId !== row.requestTypeId) disableButtonsOnEdit();
        setNewEntry((prev) => ({ ...prev, requestTypeId: chosenId }));
        setNewEntry((prev) => ({ ...prev, paymentReasonId: '' }));
        setKeys((prev) => ({ ...prev, requestReason: Math.random() }));

        clearErrors('requestTypeId,');
        clearErrors('paymentReasonId');

        if (chosenId) {
            dispatch(loadFinancialRequestReasonDetailsByRequestType(chosenId));
        }
    };

    const handleRequestReasonChange = (chosenId) => {
        if (chosenId && chosenId !== row.paymentReasonId) disableButtonsOnEdit();
        if (chosenId) {
            setNewEntry((prev) => ({ ...prev, paymentReasonId: chosenId }));
            clearErrors('paymentReasonId');
        }
    };

    const handlePaymentMethodTypeChange = (chosenId) => {
        if (chosenId && chosenId !== row.paymentMethodTypeId) disableButtonsOnEdit();
        setNewEntry((prev) => ({ ...prev, paymentMethodTypeId: chosenId }));
        setNewEntry((prev) => ({ ...prev, paymentMethodId: '' }));
        setKeys((prev) => ({ ...prev, paymentMethod: Math.random() }));

        clearErrors('paymentMethodTypeId');
        clearErrors('financialPaymentMethodId');

        if (chosenId) {
            dispatch(loadFinancialPaymentMethodDetailsByPaymentMethodType(chosenId));
        }
    };

    const handlePaymentMethodChange = (chosenId) => {
        if (chosenId && chosenId !== row.paymentMethodId) disableButtonsOnEdit();
        if (chosenId) {
            setNewEntry((prev) => ({ ...prev, paymentMethodId: chosenId }));
            clearErrors('financialPaymentMethodId');
        }
    };

    const onAuthorisationDocumentChange = (chosenId) => {
        setNewEntry((prev) => ({ ...prev, authorisationDocumentId: chosenId }));
    };

    const onEvidenceDocumentChange = (chosenId) => {
        setNewEntry((prev) => ({ ...prev, evidenceDocumentId: chosenId }));
    };

    const handleTaxChange = (option) => {
        if (option && option !== row.taxNiIssued) disableButtonsOnEdit();
        setNewEntry((prev) => ({ ...prev, taxNiIssued: option }));
    };

    const onRequestProcessedChange = (option) => {
        setNewEntry((prev) => ({ ...prev, requestProcessed: option }));
    };

    // HELPER FNS
    const clearForm = () => {
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
    };

    function disableButtonsOnEdit() {
        setApproveDisable(true);
        setDeclineDisable(true);
        setWithdrawDisable(true);
    }

    const setAdvisersArray = (advisers) => {
        const updatedAdvisers = addEmailAddressAsNameToArray(advisers);
        setAdvisers(updatedAdvisers);
    };

    const getStatusId = (name) => {
        let status = financialRequestStatusDetails?.find(
            (item) => item.name.toLowerCase() === name
        );
        return status.id;
    };

    // USE EFFECTS
    useEffect(() => {
        if (!currentParticipant?.id) return;

        if (!financialRequestStatusDetails?.length) dispatch(loadFinancialRequestStatusDetails());
        if (!financialRequestTypeDetails?.length) dispatch(loadFinancialRequestTypeDetails());
        if (!financialPaymentMethodTypeDetails?.length)
            dispatch(loadFinancialPaymentMethodTypeDetails());
        dispatch(loadFinancialRequestReasonDetailsByRequestType(row.requestTypeId));
        dispatch(loadFinancialPaymentMethodDetailsByPaymentMethodType(row.paymentMethodTypeId));
        if (
            !participantDocuments?.length ||
            participantDocuments[0].participantId !== currentParticipant.id
        )
            dispatch(loadParticipantDocumentDetails(currentParticipant.id));

        if (row.approverNote === null) row.approverNote = '';
        reset();
        setKeys(clearKeys(keys));
        setNewEntry(row);

        // Force Yup validation on pre-populated fields
        setValue('amount', row.amount, {
            shouldValidate: true
        });
        setValue('adviserId', row.adviserId, {
            shouldValidate: true
        });
        setValue('requestTypeId', row.requestTypeId, {
            shouldValidate: true
        });
        setValue('paymentReasonId', row.paymentReasonId, {
            shouldValidate: true
        });
        setValue('paymentMethodTypeId', row.paymentMethodTypeId, {
            shouldValidate: true
        });
        setValue('financialPaymentMethodId', row.paymentMethodId, {
            shouldValidate: true
        });
        setValue('reasonDetail', row.reasonDetail, {
            shouldValidate: true
        });
        if (row.approverNote)
            setValue('approverNote', row.approverNote, {
                shouldValidate: true
            });

        if (row.adviserId) {
            const initialAdviser =
                usersById[row.adviserId] || users.find((el) => el.id === row.adviserId);
            if (!initialAdviser) {
                dispatch(loadUserById(row.adviserId));
            } else {
                setAdvisersArray([initialAdviser]);
            }
        }
    }, [currentParticipant, row]);

    useEffect(() => {
        if (!users.length) return;
        let updatedAdvisers = users.filter((el) =>
            el.userTypes?.find((entry) => adviserRoles.includes(entry.role))
        );
        if (!updatedAdvisers.length && !usersMetaData?.last) {
            onLoadMoreAdvisers();
            return;
        }
        if (newEntry.adviserId && !updatedAdvisers.some((el) => el.id === newEntry.adviserId)) {
            // Put selected adviser at the top of dropdown if it's not in the updated advisers array
            const selectedAdviser = advisers.find((el) => el.id === newEntry.adviserId);
            if (selectedAdviser) updatedAdvisers = [selectedAdviser, ...updatedAdvisers];
        }
        setAdvisersArray(updatedAdvisers);
    }, [users]);

    useEffect(() => {
        if (newEntry.adviserId && usersById[newEntry.adviserId]) {
            setAdvisersArray([usersById[newEntry.adviserId]]);
        }
    }, [usersById]);

    useEffect(() => {
        if (financialRequestTypeDetails?.length && arrayFinancialRequestType?.length === 0) {
            setArrayFinancialRequestType(
                getConfiguredItems(financialRequestTypeDetails, currentParticipant?.contractId)
            );
        }
    }, [financialRequestTypeDetails]);

    useEffect(() => {
        if (financialRequestReasonDetails?.length) {
            setRequestReasonRequired(true);
            setValue('requestReasonRequired', true, {
                shouldValidate: true
            });
        } else {
            setRequestReasonRequired(false);
            setValue('requestReasonRequired', false, {
                shouldValidate: true
            });
        }
    }, [financialRequestReasonDetails]);

    useEffect(() => {
        if (financialPaymentMethodTypeDetails?.length && arrayPaymentMethodType?.length === 0) {
            setArrayPaymentMethodType(
                getConfiguredItems(
                    financialPaymentMethodTypeDetails,
                    currentParticipant?.contractId
                )
            );
        }
    }, [financialPaymentMethodTypeDetails]);

    useEffect(() => {
        if (financialPaymentMethodDetails?.length) {
            setPaymentMethodRequired(true);
            setValue('paymentMethodRequired', true, {
                shouldValidate: true
            });
        } else {
            setPaymentMethodRequired(false);
            setValue('paymentMethodRequired', false, {
                shouldValidate: true
            });
        }
    }, [financialPaymentMethodDetails]);

    useEffect(() => {
        if (!participantDocuments.length) return;
        let updatedDocuments = participantDocuments
            .filter((el) => el.status === 'READY')
            .map((el) => ({
                ...el,
                name: el.filename
            }));
        setDocuments(updatedDocuments);
    }, [participantDocuments]);

    useEffect(() => {
        if (
            successMessage === 'Participant financial request has been updated' &&
            lastSubmittedRequestId.current === row.id
        )
            clearForm();
        else if (successMessage === 'Participant financial issue request updated') {
            setShowPdfPreview(true);
        }
    }, [successMessage]);

    useEffect(() => {
        if (!newEntry.authoriserSignature || row.authoriserSignature) return;
        onAuthoriserSignatureComplete();
    }, [newEntry.authoriserSignature]);

    useEffect(() => {
        if (!participantSignature) return;
        onParticipantSignatureComplete();
    }, [participantSignature]);

    // EVENT HANDLERS
    const onSearchAuditItems = (label) => {
        setShowAuditModal(true);
        setAuditLabel(label);
    };

    const onModalClose = (e, reason) => {
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearParticipantAuditEventSearchData());
    };

    const onLoadMoreAdvisers = () => {
        if (usersMetaData?.last) return;
        dispatch(
            searchUsersByLoggedInUserServiceIds(
                loggedInUser.serviceIds,
                !users.length ? 0 : usersMetaData.number + 1
            )
        );
    };

    const onLoadMoreDocuments = () => {
        if (isLastDocumentsPage) return;
        const pageNumber = !participantDocuments.length ? 0 : documentsPageNumber + 1 || 0;
        dispatch(loadParticipantDocumentDetails(currentParticipant.id, pageNumber));
    };

    const onFormExit = () => {
        setApproveDisable(false);
        setDeclineDisable(false);
        setWithdrawDisable(false);
        payload = '';

        clearForm();
        clearErrors();
        onClose();
    };

    const onSubmit = () => {
        lastSubmittedRequestId.current = newEntry.id;
        if (payload) {
            dispatch(updateFinancialRequest(payload));
            onFormExit();
        } else {
            if (
                newEntry.amount === '0' ||
                newEntry.amount === '0.0' ||
                newEntry.amount === '0.00'
            ) {
                dispatch(setErrorMessage('Amount cannot be zero'));
            } else {
                dispatch(updateFinancialRequest(newEntry));
                onFormExit();
            }
        }
    };

    const onClosePdfPreview = () => {
        setShowPdfPreview(false);
        onFormExit();
    };

    const onParticipantSignatureComplete = () => {
        payload = {
            ...newEntry,
            dateIssued: new Date().toISOString().slice(0, 10),
            statusId: getStatusId('approved')
        };
        dispatch(updateFinancialIssueRequest(payload));
    };

    const onAuthoriserSignatureComplete = () => {
        payload = { ...newEntry, statusId: getStatusId('approved'), approverId: loggedInUser.id };
        onSubmit();
    };

    const filterParticipantDocs = (docType) => {
        return documents.filter((el) => el.type === financialRequestValidDocTypes[docType]);
    };

    function onDecline() {
        payload = { ...newEntry, statusId: getStatusId('declined'), approverId: loggedInUser.id };
        onSubmit();
    }

    function onWithdraw() {
        payload = { ...newEntry, statusId: getStatusId('withdrawn') };
        onSubmit();
    }

    let content;
    if (advisers?.length < 1) content = 'No adviser found';
    if (Object.keys(currentParticipant)?.length < 1) content = 'No current participant';

    // RENDER
    if (advisers?.length < 1 || Object.keys(currentParticipant)?.length < 1) {
        return <LoadingSpinner content={content} />;
    }

    return (
        <div className={form.formWrapper}>
            <div className={app.sectionToolBar}>
                <h2 className={app.sectionHeading}>Edit Financial Request - ID {row.code}</h2>
                <Switch
                    id={'isAuditable'}
                    label="Show Audit Fields"
                    onOff={true}
                    customClass="auditSwitch"
                    checked={isAuditable}
                    onChange={(e) => setIsAuditable(e.target.checked)}
                />
            </div>
            <form
                className={form.form}
                onSubmit={handleSubmit(onSubmit)}
                data-testid="edit_fr_form">
                {/* This is needed for request reason validations */}
                <div style={{ width: '0vh', height: '0vh', overflow: 'hidden' }}>
                    <input
                        id="requestReasonRequired"
                        name="requestReasonRequired"
                        value={requestReasonRequired}
                        {...register('requestReasonRequired')}></input>
                </div>
                {/* This is needed for payment method validations */}
                <div style={{ width: '0vh', height: '0vh', overflow: 'hidden' }}>
                    <input
                        id="paymentMethodRequired"
                        name="paymentMethodRequired"
                        value={paymentMethodRequired}
                        {...register('paymentMethodRequired')}></input>
                </div>
                <br />
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'adviserId'}
                            key={keys.adviser}
                            label={'Adviser'}
                            placeholder="Select adviser..."
                            disabled={!hasRole(adminRoles, roles) || statusName !== 'requested'}
                            mandatory={true}
                            menuItems={advisers || []}
                            selectedId={row.adviserId || ''}
                            selected={advisers?.find((el) => el.id === row.adviserId) || {}}
                            error={errors.adviserId}
                            onChange={(chosenId) => handleAdviserChange(chosenId)}
                            onLoadMoreItems={onLoadMoreAdvisers}
                            moreItemsToLoad={
                                !usersMetaData?.totalElements ||
                                users.length < usersMetaData?.totalElements
                            }
                        />
                        <LabelledTextField
                            label={'Amount'}
                            id={'amount'}
                            disabled={
                                !hasRole(
                                    [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                    roles
                                ) || statusName !== 'requested'
                            }
                            mandatory={true}
                            value={newEntry.amount || ''}
                            placeholder={'Enter amount'}
                            error={errors.amount}
                            {...register('amount')}
                            onChange={handleAmount}
                        />
                        <DDLOptionPicker
                            label={'Request Type'}
                            id={'requestTypeId'}
                            key={keys.requestType}
                            disabled={
                                !hasRole(
                                    [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                    roles
                                ) || statusName !== 'requested'
                            }
                            mandatory={true}
                            menuItems={arrayFinancialRequestType}
                            chosenName={
                                newEntry.requestTypeId
                                    ? getNameFromId(
                                          financialRequestTypeDetails,
                                          newEntry.requestTypeId
                                      )
                                    : ''
                            }
                            chosenId={newEntry.requestTypeId || ''}
                            error={errors.requestTypeId}
                            {...register('requestTypeId')}
                            onChange={(chosenId) => handleRequestTypeChange(chosenId)}
                        />
                        <DDLOptionPicker
                            label={'Reason'}
                            id={'paymentReasonId'}
                            key={keys.requestReason}
                            disabled={
                                !newEntry.requestTypeId ||
                                !hasRole(
                                    [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                    roles
                                ) ||
                                statusName !== 'requested'
                            }
                            mandatory={requestReasonRequired}
                            menuItems={financialRequestReasonDetails || []}
                            chosenName={
                                newEntry.paymentReasonId
                                    ? getNameFromId(
                                          financialRequestReasonDetails,
                                          newEntry.paymentReasonId
                                      )
                                    : ''
                            }
                            chosenId={newEntry.paymentReasonId || ''}
                            error={errors.paymentReasonId}
                            {...register('paymentReasonId')}
                            onChange={(chosenId) => handleRequestReasonChange(chosenId)}
                        />
                        <DDLOptionPicker
                            label={'Payment Method Type'}
                            id={'paymentMethodTypeId'}
                            key={keys.paymentMethodType}
                            disabled={
                                !hasRole(
                                    [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                    roles
                                ) || statusName !== 'requested'
                            }
                            mandatory={true}
                            menuItems={arrayPaymentMethodType}
                            chosenName={
                                newEntry.paymentMethodTypeId
                                    ? getNameFromId(
                                          financialPaymentMethodTypeDetails,
                                          newEntry.paymentMethodTypeId
                                      )
                                    : ''
                            }
                            chosenId={newEntry.paymentMethodTypeId || ''}
                            error={errors.paymentMethodTypeId}
                            {...register('paymentMethodTypeId')}
                            onChange={(chosenId) => handlePaymentMethodTypeChange(chosenId)}
                        />
                        <DDLOptionPicker
                            label={'Payment Method'}
                            id={'financialPaymentMethodId'}
                            key={keys.paymentMethod}
                            disabled={
                                !newEntry.paymentMethodTypeId ||
                                !hasRole(
                                    [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                    roles
                                ) ||
                                statusName !== 'requested'
                            }
                            mandatory={paymentMethodRequired}
                            menuItems={financialPaymentMethodDetails || []}
                            chosenName={
                                newEntry.paymentMethodId
                                    ? getNameFromId(
                                          financialPaymentMethodDetails,
                                          newEntry.paymentMethodId
                                      )
                                    : ''
                            }
                            chosenId={newEntry.paymentMethodId || ''}
                            error={errors.financialPaymentMethodId}
                            {...register('financialPaymentMethodId')}
                            onChange={(chosenId) => handlePaymentMethodChange(chosenId)}
                        />
                        <SingleSelect
                            id={'authorisationDocumentId'}
                            key={keys.authorisationDocumentId}
                            label={'Financial Request Authorisation'}
                            placeholder={'Search financial request authorisation...'}
                            disabled={
                                !hasRole(
                                    [
                                        ADVISER,
                                        LOCAL_ADMIN,
                                        MANAGER,
                                        RECRUITMENT_MANAGER,
                                        SUPERUSER,
                                        QUALITY
                                    ],
                                    roles
                                )
                            }
                            menuItems={filterParticipantDocs('authorisation') || []}
                            selectedId={newEntry.authorisationDocumentId}
                            selected={
                                documents.find(
                                    (el) => el.id === newEntry.authorisationDocumentId
                                ) || {}
                            }
                            onChange={(chosenId) => onAuthorisationDocumentChange(chosenId)}
                            onLoadMoreItems={onLoadMoreDocuments}
                            moreItemsToLoad={!totalDocuments || documents.length < totalDocuments}
                            isAuditable={isAuditable}
                            onSearchAuditItems={() => onSearchAuditItems('authorisationDocumentId')}
                        />
                        <SingleSelect
                            id={'evidenceDocumentId'}
                            key={keys.evidenceDocumentId}
                            label={'Financial Request Evidence'}
                            placeholder={'Search financial request evidence...'}
                            disabled={
                                !hasRole(
                                    [
                                        ADVISER,
                                        LOCAL_ADMIN,
                                        MANAGER,
                                        RECRUITMENT_MANAGER,
                                        SUPERUSER,
                                        QUALITY
                                    ],
                                    roles
                                )
                            }
                            menuItems={filterParticipantDocs('evidence')}
                            selectedId={newEntry.evidenceDocumentId}
                            selected={
                                documents.find((el) => el.id === newEntry.evidenceDocumentId) || {}
                            }
                            onChange={(chosenId) => onEvidenceDocumentChange(chosenId)}
                            onLoadMoreItems={onLoadMoreDocuments}
                            moreItemsToLoad={!totalDocuments || documents.length < totalDocuments}
                            isAuditable={isAuditable}
                            onSearchAuditItems={() => onSearchAuditItems('evidenceDocumentId')}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <LabelledTextField
                            label={'Reason Details'}
                            id={'reasonDetail'}
                            mandatory={true}
                            disabled={
                                !hasRole(
                                    [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                    roles
                                ) || statusName !== 'requested'
                            }
                            multiline
                            rows={5}
                            value={newEntry.reasonDetail}
                            placeholder={'Enter reason details'}
                            counter={'true'}
                            helperText={
                                `${newEntry.reasonDetail.length}` + '/' + REASON_DETAILS_LIMIT
                            }
                            inputProps={{ maxLength: REASON_DETAILS_LIMIT }}
                            error={errors.reasonDetail}
                            {...register('reasonDetail')}
                            onChange={(e) => {
                                disableButtonsOnEdit();
                                clearErrors('reasonDetail');
                                setNewEntry((prev) => ({ ...prev, reasonDetail: e.target.value }));
                            }}
                        />
                        <LabelledTextField
                            label={'Authoriser Notes'}
                            id={'approverNote'}
                            mandatory={true}
                            disabled={
                                !hasRole(
                                    [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                    roles
                                ) || statusName !== 'requested'
                            }
                            multiline
                            rows={5}
                            value={newEntry.approverNote}
                            placeholder={'Enter authoriser notes'}
                            counter={'true'}
                            helperText={
                                `${newEntry.approverNote.length}` + '/' + AUTHORISER_NOTES_LIMIT
                            }
                            inputProps={{ maxLength: AUTHORISER_NOTES_LIMIT }}
                            error={errors.approverNote}
                            {...register('approverNote')}
                            onChange={(e) => {
                                clearErrors('approverNote');
                                setNewEntry((prev) => ({ ...prev, approverNote: e.target.value }));
                            }}
                        />
                        <LabelledTextField
                            label={'Date Requested'}
                            id={'dateRequested'}
                            disabled={true}
                            mandatory={true}
                            value={reverseFormatDate(newEntry.dateRequested)}
                        />
                        <NoYesRadioPicker
                            id="tax"
                            disabled={
                                !hasRole(
                                    [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                    roles
                                ) || statusName !== 'requested'
                            }
                            radioButtonPick={newEntry.taxNiIssued || false}
                            label={'Tax and NI issued?'}
                            onChange={handleTaxChange}
                        />
                        {hasBeenIssued && (
                            <NoYesRadioPicker
                                id="requestProcessed"
                                disabled={
                                    !hasRole([LOCAL_ADMIN, MANAGER, QUALITY, SUPERUSER], roles)
                                }
                                radioButtonPick={newEntry.requestProcessed || false}
                                label={'Request Processed (Finance only)'}
                                onChange={onRequestProcessedChange}
                                isAuditable={isAuditable}
                                onSearchAuditItems={() => onSearchAuditItems('requestProcessed')}
                            />
                        )}
                    </div>
                </div>

                {showAuditModal && (
                    <AuditFinancialRequestsModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        requestId={newEntry.id}
                        onClose={onModalClose}
                    />
                )}

                <div className={actions.formActions}>
                    <Button
                        type="button"
                        color="primary"
                        variant="contained"
                        onClick={() => setAuthorisersSignatureDialogOpen(true)}
                        disabled={
                            !hasRole([MANAGER, QUALITY, SUPERUSER], roles) ||
                            statusName !== 'requested' ||
                            approveDisable ||
                            newEntry.approverNote.length < 7
                        }>
                        {' '}
                        Approve
                    </Button>
                    <Button
                        type="button"
                        color="primary"
                        variant="contained"
                        onClick={onDecline}
                        disabled={
                            !hasRole([MANAGER, QUALITY, SUPERUSER], roles) ||
                            statusName !== 'requested' ||
                            declineDisable ||
                            newEntry.approverNote.length < 7
                        }>
                        {' '}
                        Decline
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        onClick={onWithdraw}
                        disabled={
                            !hasRole(
                                [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                roles
                            ) ||
                            (!(
                                newEntry.dateIssued &&
                                hasRole([SUPERUSER, QUALITY], roles) &&
                                statusName !== 'withdrawn'
                            ) &&
                                statusName !== 'requested' &&
                                statusName !== 'approved') ||
                            (newEntry.dateIssued &&
                                statusName === 'approved' &&
                                !hasRole([SUPERUSER, QUALITY], roles)) ||
                            withdrawDisable ||
                            newEntry.approverNote.length < 7
                        }>
                        Withdraw
                    </Button>
                    <Button
                        type="button"
                        color="primary"
                        variant="contained"
                        onClick={() => setParticipantSignatureDialogOpen(true)}
                        disabled={
                            !hasRole(
                                [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                roles
                            ) ||
                            statusName !== 'approved' ||
                            (statusName === 'approved' && newEntry.dateIssued)
                        }>
                        {' '}
                        Issue
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={
                            !hasRole(
                                [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER],
                                roles
                            )
                        }>
                        {' '}
                        Update
                    </Button>
                    <div className={actions.cancelLink} onClick={onFormExit}>
                        Cancel X
                    </div>
                </div>
            </form>

            <ESignatureModal
                dialogOpen={participantSignatureDialogOpen}
                onSignatureModalClose={() => setParticipantSignatureDialogOpen(false)}
                onSignatureSave={(signature) => setParticipantSignature(signature)}
                onSignatureSkip={onParticipantSignatureComplete}
                signerName={'Participant'}
            />

            <ESignatureModal
                dialogOpen={authorisersSignatureDialogOpen}
                onSignatureModalClose={() => setAuthorisersSignatureDialogOpen(false)}
                onSignatureSave={(signature) =>
                    setNewEntry({ ...newEntry, authoriserSignature: signature })
                }
                onSignatureSkip={onAuthoriserSignatureComplete}
                signerName={'Authoriser'}
            />
            {showPdfPreview && (
                <Dialog fullWidth={true} maxWidth={'md'} open={true}>
                    <DialogTitle id="id">Preview PDF</DialogTitle>
                    <GenerateFinancialRequestPDF
                        financialRequest={newEntry}
                        participantSignature={participantSignature}
                    />
                    <FormButton type="button" content="Close" onClick={onClosePdfPreview} />
                </Dialog>
            )}
        </div>
    );
};

const validationSchema = Yup.object().shape({
    adviserId: Yup.string().required('Please select an adviser'),
    amount: Yup.string()
        .nullable()
        .min(0.1, 'Amount cannot be negative, blank or zero')
        .max(9999.99, 'Amount must be 9999.99 or less')
        .matches(regExps.PRICE_R, 'Invalid Amount'),
    requestTypeId: Yup.string().required('Please select a request type'),
    requestReasonRequired: Yup.boolean(),
    paymentMethodTypeId: Yup.string().required('Please select a payment method type'),
    paymentMethodRequired: Yup.boolean(),
    paymentReasonId: Yup.string()
        .nullable()
        .when('requestReasonRequired', {
            is: (requestReasonRequired) => requestReasonRequired === true,
            then: () => Yup.string().required('Please select a reason')
        }),
    financialPaymentMethodId: Yup.string()
        .nullable()
        .when('paymentMethodRequired', {
            is: (paymentMethodRequired) => paymentMethodRequired === true,
            then: () => Yup.string().required('Please select a payment method')
        }),
    reasonDetail: Yup.string().min(7, 'Reason details must be at least seven characters'),
    approverNote: Yup.string().min(7, 'Authoriser note must be at least seven characters')
});

EditFinancialRequest.propTypes = {
    row: PropTypes.object,
    roles: PropTypes.arrayOf(PropTypes.string),
    onClose: PropTypes.func
};

export default EditFinancialRequest;
